import React, { useEffect, useMemo, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import Select from "react-select";
import { useLazyQuery } from "@apollo/client";

import { HAL_KEY, JNT_KEY, LOGISTICS_DATAS, MIXAY_KEY } from "../../../constants";
import {
  GET_HAL_BRANCHS,
  GET_JNT_PROVINCES,
  GET_MIXAY_DROP_SHIPS_V2,
  GET_MIXAY_PROVINCES_V2,
} from "../../../graphql/shop/shopAddress/query";
import { formatDeliveryAddress } from "../../../helpers";

//* ເລືອກຂົນສົ່ງ + ທີ່ຢູ່ປາຍທາງ ໃນໜ້າສ້າງອໍເດີ — ແຫຼ່ງຂໍ້ມູນ ແລະ ການກອງ ຄືກັບ FormReceiveAddress
//* ແຕ່ບໍ່ບັນທຶກຫຍັງລົງຖານຂໍ້ມູນ: ສົ່ງກັບແຕ່ຂໍ້ຄວາມ { logistic, address } ໃຫ້ອໍເດີ
export const MANUAL_KEY = "MANUAL";
const MANUAL_LOGISTIC_NAME = "ພິມທີ່ຢູ່ເອງ"; // ຄືກັບໜ້າຢືນຢັນອໍເດີ (ShopInfoOrderConfirm)

const CARRIERS = [
  { key: HAL_KEY, label: LOGISTICS_DATAS.hal.name, logistic: LOGISTICS_DATAS.hal.name },
  { key: MIXAY_KEY, label: LOGISTICS_DATAS.mixay.name, logistic: LOGISTICS_DATAS.mixay.name },
  { key: JNT_KEY, label: "J&T", logistic: LOGISTICS_DATAS.jnt.name },
  { key: MANUAL_KEY, label: "ປ້ອນເອງ", logistic: MANUAL_LOGISTIC_NAME },
];

//* ຊື່ຂົນສົ່ງທີ່ບັນທຶກໄວ້ໃນອໍເດີ → key (ໃຊ້ຕອນແກ້ໄຂ)
export const carrierKeyOfLogistic = (logistic) =>
  CARRIERS.find((c) => c.logistic === logistic)?.key || "";

const labelStyle = { marginBottom: 2, fontWeight: "bold", color: "#5C5C5C" };

//* ແຖວ: ປ້າຍຊື່ຢູ່ດ້ານໜ້າ + ຊ່ອງເລືອກ/ພິມ
const FieldRow = ({ label, children }) => (
  <div className="d-flex align-items-center gap-2">
    <div style={{ width: 56, flexShrink: 0, fontSize: 14, color: "#5C5C5C" }}>{label}</div>
    <div style={{ flex: 1, minWidth: 0 }}>{children}</div>
  </div>
);
const toOptions = (list) => (list || []).map((x) => ({ value: x.id, label: x.name }));
const parseJson = (s) => {
  try {
    return JSON.parse(s || "[]");
  } catch (e) {
    return [];
  }
};

export default function DestinationPicker({ currentLogistic, currentAddress, onChange }) {
  const [carrier, setCarrier] = useState(() => carrierKeyOfLogistic(currentLogistic));
  const [province, setProvince] = useState(null);
  const [district, setDistrict] = useState(null);
  const [branch, setBranch] = useState(null); // option ຂອງສາຂາ (ຂົນສົ່ງ)
  const [manualBranch, setManualBranch] = useState(""); // ສາຂາ/ທີ່ຢູ່ ທີ່ພິມເອງ
  const [manualCarrier, setManualCarrier] = useState(""); // ຊື່ຂົນສົ່ງ ທີ່ພິມເອງ
  const [mixayDropShips, setMixayDropShips] = useState([]);
  const [touched, setTouched] = useState(false);

  // HAL ໃຊ້ທັງ ຮຸ່ງອາລຸນ ແລະ ປ້ອນເອງ (ລາຍຊື່ແຂວງ/ເມືອງທົ່ວປະເທດ)
  const [getHal, { data: halData, loading: halLoading }] = useLazyQuery(GET_HAL_BRANCHS, {
    fetchPolicy: "cache-first",
  });
  const [getJnt, { data: jntData, loading: jntLoading }] = useLazyQuery(GET_JNT_PROVINCES, {
    fetchPolicy: "cache-first",
  });
  const [getMixayProvinces, { data: mixayProvinceData, loading: mixayLoading }] = useLazyQuery(
    GET_MIXAY_PROVINCES_V2,
    { fetchPolicy: "cache-first" },
  );
  const [getMixayDropShips, { loading: dropShipLoading }] = useLazyQuery(GET_MIXAY_DROP_SHIPS_V2, {
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (carrier === HAL_KEY || carrier === MANUAL_KEY) getHal();
    if (carrier === JNT_KEY) getJnt();
    if (carrier === MIXAY_KEY) getMixayProvinces();
  }, [carrier]);

  const hal = halData?.getHalBranchList;
  const jnt = jntData?.getJntProvinces;
  const halBranches = useMemo(() => parseJson(hal?.branchDatas), [hal?.branchDatas]);
  const jntBranches = useMemo(() => parseJson(jnt?.branchDatas), [jnt?.branchDatas]);

  // ── ຕົວເລືອກ ແຂວງ / ເມືອງ / ສາຂາ ຕາມຂົນສົ່ງ ──
  const provinceOptions = useMemo(() => {
    if (carrier === HAL_KEY || carrier === MANUAL_KEY) return toOptions(hal?.provinces);
    if (carrier === JNT_KEY) return toOptions(jnt?.provinces);
    if (carrier === MIXAY_KEY) return toOptions(mixayProvinceData?.getMixayProvincesV2?.provinces);
    return [];
  }, [carrier, hal, jnt, mixayProvinceData]);

  const districtOptions = useMemo(() => {
    if (!province) return [];
    if (carrier === HAL_KEY || carrier === MANUAL_KEY)
      return toOptions((hal?.districts || []).filter((d) => d?.province_id === province.value));
    if (carrier === JNT_KEY)
      return toOptions((jnt?.districts || []).filter((d) => d?.province_id === province.value));
    if (carrier === MIXAY_KEY) {
      // ມີໄຊ: ເມືອງ = dr_id ທີ່ບໍ່ຊ້ຳ ຈາກ drop-ship ຂອງແຂວງນັ້ນ
      const seen = new Set();
      return mixayDropShips
        .filter((d) => {
          const id = d?.dristric?.dr_id;
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        })
        .map((d) => ({ value: d?.dristric?.dr_id, label: d?.dristric?.dr_name }));
    }
    return [];
  }, [carrier, province, hal, jnt, mixayDropShips]);

  const branchOptions = useMemo(() => {
    if (!district) return [];
    if (carrier === HAL_KEY)
      return halBranches
        .filter((b) => Number(b?.district_id) === Number(district.value))
        .map((b) => ({ value: b?.id, label: b?.name }));
    if (carrier === JNT_KEY)
      // district_id ຂອງ J&T ເປັນ cityCode ("0101") — ທຽບແບບ string
      return jntBranches
        .filter((b) => String(b?.district_id) === String(district.value))
        .map((b) => ({ value: b?.id, label: b?.name }));
    if (carrier === MIXAY_KEY)
      return mixayDropShips
        .filter((b) => b?.dristric?.dr_id === district.value)
        .map((b) => ({ value: b?.code, label: b?.name }));
    return [];
  }, [carrier, district, halBranches, jntBranches, mixayDropShips]);

  // ── ຕອນແກ້ໄຂ: ເຕີມຂົນສົ່ງ + ແຂວງ/ເມືອງ/ສາຂາ ທີ່ເຄີຍເລືອກ ລົງ select ໃຫ້ເລີຍ ──
  //* ທີ່ຢູ່ບັນທຶກເປັນ "ສາຂາ, ເມືອງ, ແຂວງ" (formatDeliveryAddress) → ແຍກແລ້ວຈັບຄູ່ກັບຕົວເລືອກຕາມຊື່
  //* ບໍ່ຕັ້ງ touched → ບໍ່ສົ່ງຄ່າໃໝ່ໃຫ້ອໍເດີ ຈົນກວ່າຜູ້ໃຊ້ຈະແກ້ເອງ
  const pendingRef = useRef(null); // { province, district, branch } ທີ່ຍັງລໍຖ້າຈັບຄູ່
  const initRef = useRef(false);
  useEffect(() => {
    if (initRef.current || touched || !currentLogistic) return;
    initRef.current = true;
    let key = carrierKeyOfLogistic(currentLogistic);
    if (!key) {
      //* ຊື່ຂົນສົ່ງທີ່ພິມເອງ (ບໍ່ແມ່ນ ຮຸ່ງອາລຸນ/ມີໄຊ/J&T) → ປ້ອນເອງ
      key = MANUAL_KEY;
      setManualCarrier(currentLogistic);
    }
    setCarrier(key);
    const parts = String(currentAddress || "")
      .split(",")
      .map((x) => x.trim())
      .filter(Boolean);
    if (parts.length >= 2) {
      pendingRef.current = {
        province: parts[parts.length - 1],
        district: parts[parts.length - 2],
        branch: parts.slice(0, -2).join(", "),
      };
    } else if (key === MANUAL_KEY && parts.length === 1) {
      setManualBranch(parts[0]);
    }
  }, [currentLogistic, currentAddress, touched]);

  // ແຂວງ
  useEffect(() => {
    const p = pendingRef.current;
    if (!p?.province || touched || province || !provinceOptions.length) return;
    const opt = provinceOptions.find((o) => o.label === p.province);
    pendingRef.current = { ...p, province: "" };
    if (!opt) return;
    setProvince(opt);
    if (carrier === MIXAY_KEY) {
      getMixayDropShips({ variables: { where: { provinceId: opt.value } } }).then((res) =>
        setMixayDropShips(parseJson(res?.data?.getMixayDropShipsV2?.branchDatas)),
      );
    }
  }, [provinceOptions, touched]);

  // ເມືອງ
  useEffect(() => {
    const p = pendingRef.current;
    if (!p?.district || touched || !province || district || !districtOptions.length) return;
    const opt = districtOptions.find((o) => o.label === p.district);
    pendingRef.current = { ...p, district: "" };
    if (opt) setDistrict(opt);
  }, [districtOptions, province, touched]);

  // ສາຂາ (ປ້ອນເອງ = ຂໍ້ຄວາມ)
  useEffect(() => {
    const p = pendingRef.current;
    if (!p?.branch || touched || !district) return;
    if (carrier === MANUAL_KEY) {
      setManualBranch(p.branch);
      pendingRef.current = null;
      return;
    }
    if (!branchOptions.length) return;
    const opt = branchOptions.find((o) => o.label === p.branch);
    pendingRef.current = null;
    if (opt) setBranch(opt);
  }, [branchOptions, district, touched]);

  // ── ສົ່ງຂໍ້ຄວາມໃຫ້ອໍເດີ (ສະເພາະຫຼັງຜູ້ໃຊ້ເລືອກແລ້ວ — ຕອນແກ້ໄຂ ບໍ່ແຕະຄ່າເດີມ) ──
  useEffect(() => {
    if (!touched) return;
    const c = CARRIERS.find((x) => x.key === carrier);
    const branchText = carrier === MANUAL_KEY ? manualBranch.trim() : branch?.label;
    onChange({
      logistic: carrier === MANUAL_KEY ? manualCarrier.trim() || MANUAL_LOGISTIC_NAME : c?.logistic || "",
      address: formatDeliveryAddress({
        branch: branchText,
        district: district?.label,
        province: province?.label,
      }),
    });
  }, [touched, carrier, province, district, branch, manualBranch, manualCarrier]);

  const selectCarrier = (key) => {
    setTouched(true);
    setCarrier(key);
    setProvince(null);
    setDistrict(null);
    setBranch(null);
    setManualBranch("");
    setManualCarrier("");
    setMixayDropShips([]);
  };

  const selectProvince = async (opt) => {
    setTouched(true);
    setProvince(opt);
    setDistrict(null);
    setBranch(null);
    if (carrier === MIXAY_KEY) {
      setMixayDropShips([]);
      if (!opt) return;
      const res = await getMixayDropShips({ variables: { where: { provinceId: opt.value } } });
      setMixayDropShips(parseJson(res?.data?.getMixayDropShipsV2?.branchDatas));
    }
  };

  const loadingProvince =
    (carrier === HAL_KEY || carrier === MANUAL_KEY ? halLoading : false) ||
    (carrier === JNT_KEY ? jntLoading : false) ||
    (carrier === MIXAY_KEY ? mixayLoading : false);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label style={labelStyle}>ຂົນສົ່ງ</Form.Label>
        <Form.Select
          name="logistic"
          value={carrier}
          onChange={(e) => selectCarrier(e.target.value)}
        >
          <option value="" disabled>
            ເລືອກຂົນສົ່ງ...
          </option>
          {CARRIERS.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </Form.Select>
        {carrier === MANUAL_KEY && (
          <Form.Control
            className="mt-2"
            type="text"
            placeholder="ພິມຊື່ຂົນສົ່ງ... (ເຊັ່ນ ອານຸສິດ)"
            value={manualCarrier}
            onChange={(e) => {
              setTouched(true);
              setManualCarrier(e.target.value);
            }}
          />
        )}
        {!carrier && currentLogistic && (
          <Form.Text muted>ຂົນສົ່ງເດີມ: {currentLogistic}</Form.Text>
        )}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label style={labelStyle}>ທີ່ຢູ່ປາຍທາງ</Form.Label>
        {!touched && currentAddress && !province && !manualBranch && (
          <div style={{ fontSize: 13, color: "#6B7280", marginBottom: 6 }}>
            ທີ່ຢູ່ເດີມ: {currentAddress}
          </div>
        )}
        {!carrier ? (
          <div style={{ fontSize: 13, color: "#9CA3AF" }}>ເລືອກຂົນສົ່ງກ່ອນ</div>
        ) : (
          <div className="d-flex flex-column gap-2">
            <FieldRow label="ແຂວງ">
            <Select
              placeholder="ເລືອກແຂວງ..."
              options={provinceOptions}
              value={province}
              onChange={selectProvince}
              isLoading={loadingProvince}
              isClearable
            />
            </FieldRow>
            <FieldRow label="ເມືອງ">
            <Select
              placeholder="ເລືອກເມືອງ..."
              options={districtOptions}
              value={district}
              onChange={(opt) => {
                setTouched(true);
                setDistrict(opt);
                setBranch(null);
              }}
              isDisabled={!province}
              isLoading={carrier === MIXAY_KEY && dropShipLoading}
              isClearable
            />
            </FieldRow>
            <FieldRow label="ສາຂາ">
            {carrier === MANUAL_KEY ? (
              <Form.Control
                type="text"
                placeholder="ພິມສາຂາ / ບ້ານ / ທີ່ຢູ່..."
                value={manualBranch}
                onChange={(e) => {
                  setTouched(true);
                  setManualBranch(e.target.value);
                }}
              />
            ) : (
              <Select
                placeholder="ເລືອກສາຂາ..."
                options={branchOptions}
                value={branch}
                onChange={(opt) => {
                  setTouched(true);
                  setBranch(opt);
                }}
                isDisabled={!district}
                noOptionsMessage={() => "ບໍ່ມີສາຂາໃນເມືອງນີ້"}
                isClearable
              />
            )}
            </FieldRow>
          </div>
        )}
      </Form.Group>
    </>
  );
}
